import { useEffect, useState } from "react";
import { trackEvent } from "@/lib/audit";

/**
 * PR-16 — Mobile Battery Optimization Mode
 *
 * Centralized engine that controls render FPS targets, animation richness,
 * polling cadence, and audit batching to dramatically reduce battery drain
 * on mobile and tablet devices without breaking the core demo experience.
 */

export type BatteryMode = "performance" | "balanced" | "battery_saver";
export type DeviceProfile = "desktop" | "mobile" | "tablet";

const KEY = "vs:battery_mode";
const EVT = "vs:battery_mode_changed";

let currentMode: BatteryMode = "balanced";
let lowBatteryAuto = false;
let lastBatteryLevel: number | null = null;
let lastRenderMs = 0;
let lastMeasuredFps = 0;

function detectDeviceProfile(): DeviceProfile {
  if (typeof window === "undefined") return "desktop";
  const w = window.innerWidth;
  if (w < 768) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

export function getDeviceProfile(): DeviceProfile {
  return detectDeviceProfile();
}

export function isMobileOrTablet(): boolean {
  const p = detectDeviceProfile();
  return p === "mobile" || p === "tablet";
}

/** Resolve the effective mode: explicit user choice wins, otherwise auto. */
export function getBatteryMode(): BatteryMode {
  return currentMode;
}

export function setBatteryMode(mode: BatteryMode, opts: { auto?: boolean } = {}) {
  if (typeof window === "undefined") return;
  const prev = currentMode;
  currentMode = mode;
  if (!opts.auto) {
    try { window.localStorage.setItem(KEY, mode); } catch { /* noop */ }
  }
  if (prev !== mode) {
    void trackEvent("battery_mode_changed", { from: prev, to: mode, auto: !!opts.auto });
    window.dispatchEvent(new CustomEvent(EVT, { detail: { mode } }));
  }
}

/** Initialize from storage + device auto-detection + Battery API. Call once at app boot. */
export function initBatteryMode() {
  if (typeof window === "undefined") return;
  let stored: BatteryMode | null = null;
  try { stored = window.localStorage.getItem(KEY) as BatteryMode | null; } catch { /* noop */ }
  if (stored === "performance" || stored === "balanced" || stored === "battery_saver") {
    currentMode = stored;
  } else {
    // Auto: mobile/tablet defaults to balanced (with mobile-eco throttling baked in).
    currentMode = "balanced";
  }
  // Battery API integration (Chromium / some mobile browsers).
  const nav = navigator as Navigator & {
    getBattery?: () => Promise<{
      level: number;
      charging: boolean;
      addEventListener: (t: string, fn: () => void) => void;
    }>;
  };
  if (typeof nav.getBattery === "function") {
    nav.getBattery().then((b) => {
      const sync = () => {
        lastBatteryLevel = b.level;
        if (b.level < 0.2 && !b.charging && currentMode !== "battery_saver") {
          lowBatteryAuto = true;
          void trackEvent("low_battery_detected", { level: b.level });
          void trackEvent("battery_saver_auto_enabled", { level: b.level });
          setBatteryMode("battery_saver", { auto: true });
        } else if (b.level >= 0.3 && lowBatteryAuto) {
          // Recovered — release auto saver.
          lowBatteryAuto = false;
          setBatteryMode("balanced", { auto: true });
        }
      };
      sync();
      b.addEventListener("levelchange", sync);
      b.addEventListener("chargingchange", sync);
    }).catch(() => { /* noop */ });
  }
  // Pause/resume notifications when tab is hidden.
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) {
      void trackEvent("background_render_paused", { mode: currentMode });
    }
  });
}

/** Target FPS for the radar render loop based on mode + device profile. */
export function getTargetFps(): number {
  const mode = currentMode;
  const device = detectDeviceProfile();
  if (mode === "battery_saver") return 15;
  if (mode === "performance") return device === "desktop" ? 60 : 30;
  // balanced
  if (device === "desktop") return 60;
  return 24; // mobile/tablet
}

/** Polling interval (ms) for periodic refreshes (odds, scores, etc.). */
export function getPollingIntervalMs(): number {
  return currentMode === "battery_saver" ? 45_000 : 20_000;
}

/** Audit flush interval (ms). Higher in saver mode for batching. */
export function getAuditFlushMs(): number {
  return currentMode === "battery_saver" ? 60_000 : 5_000;
}

/** Render should pause entirely when the tab is hidden. */
export function shouldPauseRender(): boolean {
  return typeof document !== "undefined" && document.hidden;
}

/** Disable non-essential effects (confetti, heavy particles, blur). */
export function effectsEnabled(): boolean {
  return currentMode !== "battery_saver";
}

/** Whether costly GPU effects (shadows, glow) should run. */
export function gpuEffectsEnabled(): boolean {
  if (currentMode === "battery_saver") return false;
  if (currentMode === "balanced" && isMobileOrTablet()) return false;
  return true;
}

/** Report a render frame duration so the admin dashboard can show stats. */
export function reportRenderFrame(deltaMs: number) {
  lastRenderMs = deltaMs;
  lastMeasuredFps = deltaMs > 0 ? Math.round(1000 / deltaMs) : 0;
}

export function getRenderStats() {
  return {
    fps: lastMeasuredFps,
    renderMs: lastRenderMs,
    targetFps: getTargetFps(),
    batteryLevel: lastBatteryLevel,
    device: detectDeviceProfile(),
    mode: currentMode,
  };
}

/** React hook — subscribe to mode changes. */
export function useBatteryMode(): BatteryMode {
  const [mode, setMode] = useState<BatteryMode>(currentMode);
  useEffect(() => {
    const h = (e: Event) => setMode((e as CustomEvent<{ mode: BatteryMode }>).detail.mode);
    window.addEventListener(EVT, h);
    return () => window.removeEventListener(EVT, h);
  }, []);
  return mode;
}

/** React hook — live render stats for the admin dashboard (1Hz). */
export function useRenderStats() {
  const [stats, setStats] = useState(getRenderStats());
  useEffect(() => {
    const id = window.setInterval(() => setStats(getRenderStats()), 1000);
    return () => window.clearInterval(id);
  }, []);
  return stats;
}

// ──────────────────────────────────────────────────────────────────────────
// FPS-reduction notice (one-shot per session) to keep audit log clean.
// ──────────────────────────────────────────────────────────────────────────
let fpsReducedNotified = false;
export function noteFpsReducedOnce(target: number) {
  if (fpsReducedNotified) return;
  fpsReducedNotified = true;
  void trackEvent("render_fps_reduced", { target_fps: target, mode: currentMode });
}